import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, BriefcaseBusiness, Building2, CheckCircle2, Hash, Loader2, Mail, Network, Phone, Save, ShieldCheck, User, UserPlus, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const decodeToken = (token) => {
    try {
        const payload = token?.split(".")?.[1];
        if (!payload)
            return null;
        const normalized = payload
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(payload.length / 4) * 4, "=");
        return JSON.parse(atob(normalized));
    }
    catch {
        return null;
    }
};
const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    jobTitle: "",
    department: "",
    externalId: "",
};
const AddParticipant = () => {
    const [formData, setFormData] = useState(initialForm);
    const [clientId, setClientId] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const navigate = useNavigate();
    const { t } = useLanguage();
    const tr = (key, fallback) => t?.ui?.addParticipant?.[key] || t?.[key] || fallback;
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate("/login", { replace: true });
            return;
        }
        const tokenData = decodeToken(token);
        let storedUser = null;
        try {
            storedUser = JSON.parse(localStorage.getItem("user") || "null");
        }
        catch {
            storedUser = null;
        }
        const userType = tokenData?.type || storedUser?.type;
        const userId = tokenData?.id || tokenData?._id || storedUser?.id || storedUser?._id;
        // Permission conservée : cette page reste réservée au rôle Client.
        if (userType !== "Client") {
            navigate("/unauthorized", { replace: true });
            return;
        }
        setClientId(userId || "");
        setCheckingAuth(false);
    }, [navigate]);
    const updateField = (field) => (event) => {
        setFormData((current) => ({
            ...current,
            [field]: event.target.value,
        }));
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setMessage("");
        setError("");
        const firstName = formData.firstName.trim();
        const lastName = formData.lastName.trim();
        const email = formData.email.trim().toLowerCase();
        if (!firstName || !lastName || !email) {
            setError(tr("requiredError", "Le prénom, le nom et l’email sont obligatoires."));
            setLoading(false);
            return;
        }
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/participants`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...formData,
                    firstName,
                    lastName,
                    name: `${firstName} ${lastName}`.trim(),
                    email,
                    client_id: clientId,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message ||
                    t?.errorAddingParticipant ||
                    "Impossible d'ajouter ce participant.");
            }
            setMessage(data.message ||
                `${t?.participantWord || "Participant"} ${firstName} ${lastName} ${t?.addedSuccessSuffix || "ajouté avec succès"}.`);
            setFormData(initialForm);
            window.setTimeout(() => {
                navigate("/participants");
            }, 900);
        }
        catch (submitError) {
            setError(submitError.message || t?.serverError || "Erreur serveur.");
        }
        finally {
            setLoading(false);
        }
    };
    if (checkingAuth) {
        return (<div className="flex min-h-[70vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-700 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600"/>
          {tr("checkingPermissions", "Vérification des permissions...")}
        </div>
      </div>);
    }
    const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100";
    return (<main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <button type="button" onClick={() => navigate("/participants")} className="mb-5 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
          <ArrowLeft className="h-4 w-4"/>
          {tr("backParticipants", "Retour aux participants")}
        </button>

        <div className="grid overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 lg:grid-cols-[0.78fr_1.22fr]">
          <section className="relative overflow-hidden bg-slate-950 p-7 text-white sm:p-9 lg:p-10">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-blue-600/25 blur-3xl"/>
            <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl"/>

            <div className="relative">
              <div className="inline-flex rounded-2xl bg-blue-600 p-3 shadow-lg shadow-blue-950/30">
                <UserPlus className="h-7 w-7"/>
              </div>
              <p className="mt-7 text-xs font-black uppercase tracking-[0.2em] text-cyan-300">
                CertiLink
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
                {t?.addParticipantTitle || "Ajouter un participant"}
              </h1>
              <p className="mt-4 max-w-md text-sm leading-7 text-slate-300">{tr("description", "Enregistrez les informations essentielles du participant afin de centraliser son profil et de lui associer un ou plusieurs certificats.")}</p>

              <div className="mt-9 space-y-4">
                {[
            [ShieldCheck, tr("benefitAccount", "Donn\u00E9es reli\u00E9es \u00E0 votre compte client")],
            [User, tr("benefitLegacy", "Identit\u00E9 structur\u00E9e et compatible avec les anciens profils")],
            [CheckCircle2, tr("benefitReady", "Profil imm\u00E9diatement disponible pour la certification")],
        ].map(([Icon, label]) => (<div key={label} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-cyan-300">
                      <Icon className="h-4 w-4"/>
                    </span>
                    {label}
                  </div>))}
              </div>
            </div>
          </section>

          <section className="p-6 sm:p-8 lg:p-10">
            <div className="mb-7 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">{tr("newProfile", "Nouveau profil")}</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950">{tr("participantInfo", "Informations du participant")}</h2>
                <p className="mt-2 text-sm text-slate-500">{tr("requiredFields", "Les champs marqu\u00E9s d\u2019un ast\u00E9risque sont obligatoires.")}</p>
              </div>
              <span className="hidden rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 sm:inline-flex">{tr("authenticatedClient", "Client authentifi\u00E9")}</span>
            </div>

            {message && (<div className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0"/>
                {message}
              </div>)}

            {error && (<div className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-800">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0"/>
                {error}
              </div>)}

            <form onSubmit={handleSubmit} className="space-y-7">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600"/>
                  <h3 className="font-black text-slate-900">{tr("identity", "Identit\u00E9")}</h3>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block text-sm font-bold text-slate-700">{tr("firstName", "Pr\u00E9nom *")}<input type="text" value={formData.firstName} onChange={updateField("firstName")} placeholder={tr("firstNamePlaceholder", "Ahmed")} required className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700">{tr("lastName", "Nom *")}<input type="text" value={formData.lastName} onChange={updateField("lastName")} placeholder={tr("lastNamePlaceholder", "Ben Ali")} required className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700 sm:col-span-2">
                    <span className="inline-flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400"/>
                      {tr("email", "Adresse email")} *
                    </span>
                    <input type="email" value={formData.email} onChange={updateField("email")} placeholder={t?.emailPlaceholder || "participant@example.com"} required className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700 sm:col-span-2">
                    <span className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400"/>{tr("phone", "T\u00E9l\u00E9phone")}</span>
                    <input type="tel" value={formData.phone} onChange={updateField("phone")} placeholder="+216 00 000 000" className={`${inputClass} mt-2`}/>
                  </label>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-7">
                <div className="mb-4 flex items-center gap-2">
                  <BriefcaseBusiness className="h-5 w-5 text-cyan-600"/>
                  <h3 className="font-black text-slate-900">{tr("professionalInfo", "Informations professionnelles")}</h3>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block text-sm font-bold text-slate-700">
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400"/>{tr("organizationPlaceholder", "Organisation")}</span>
                    <input type="text" value={formData.organization} onChange={updateField("organization")} placeholder={tr("organizationPlaceholder", "Organisation")} className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700">
                    <span className="inline-flex items-center gap-2">
                      <BriefcaseBusiness className="h-4 w-4 text-slate-400"/>{tr("jobTitle", "Fonction")}</span>
                    <input type="text" value={formData.jobTitle} onChange={updateField("jobTitle")} placeholder={tr("jobTitlePlaceholder", "Chef de projet")} className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700">
                    <span className="inline-flex items-center gap-2">
                      <Network className="h-4 w-4 text-slate-400"/>{tr("department", "D\u00E9partement")}</span>
                    <input type="text" value={formData.department} onChange={updateField("department")} placeholder={tr("departmentPlaceholder", "Formation")} className={`${inputClass} mt-2`}/>
                  </label>
                  <label className="block text-sm font-bold text-slate-700">
                    <span className="inline-flex items-center gap-2">
                      <Hash className="h-4 w-4 text-slate-400"/>{tr("externalId", "Identifiant externe")}</span>
                    <input type="text" value={formData.externalId} onChange={updateField("externalId")} placeholder="PART-2026-001" className={`${inputClass} mt-2`}/>
                  </label>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-7 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => navigate("/participants")} className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50">
                  {t?.cancel || "Annuler"}
                </button>
                <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
                  {loading ? (<Loader2 className="h-4 w-4 animate-spin"/>) : (<Save className="h-4 w-4"/>)}
                  {loading
            ? t?.adding || "Ajout en cours..."
            : t?.addParticipantButton || "Ajouter le participant"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </main>);
};
export default AddParticipant;
